import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EmailTemplateApi } from '../services/EmailTemplateApi';

interface TestEmailState {
  testEmail: string;
  testData: string;
  jsonError: string;
  previewContent: {
    subject: string;
    html: string;
    text: string;
  };
  activeTab: number;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: TestEmailState = {
  testEmail: '',
  testData: '{}',
  jsonError: '',
  previewContent: {
    subject: '',
    html: '',
    text: '',
  },
  activeTab: 0,
  loading: false,
  error: null,
  success: false,
};

// Async thunk for sending test email
export const sendTestEmailAsync = createAsyncThunk(
  'emailTestModal/sendTestEmail',
  async (
    payload: { templateId: string; testEmail: string; testData: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const response = await EmailTemplateApi.sendTestEmail(payload.templateId, {
        testEmail: payload.testEmail,
        testData: payload.testData,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send test email');
    }
  }
);

const emailTestModalSlice = createSlice({
  name: 'emailTestModal',
  initialState,
  reducers: {
    setTestEmail: (state, action: PayloadAction<string>) => {
      state.testEmail = action.payload;
    },
    setTestData: (state, action: PayloadAction<string>) => {
      state.testData = action.payload;
    },
    setJsonError: (state, action: PayloadAction<string>) => {
      state.jsonError = action.payload;
    },
    setPreviewContent: (
      state,
      action: PayloadAction<{ subject: string; html: string; text: string }>
    ) => {
      state.previewContent = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload;
    },
    resetModal: (state) => {
      state.testEmail = '';
      state.testData = '{}';
      state.jsonError = '';
      state.previewContent = {
        subject: '',
        html: '',
        text: '',
      };
      state.activeTab = 0;
      state.error = null;
      state.success = false;
    },
    initializeTestData: (state, action: PayloadAction<string>) => {
      state.testData = action.payload;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendTestEmailAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(sendTestEmailAsync.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(sendTestEmailAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const {
  setTestEmail,
  setTestData,
  setJsonError,
  setPreviewContent,
  setActiveTab,
  resetModal,
  initializeTestData,
  clearSuccess,
} = emailTestModalSlice.actions;

export default emailTestModalSlice.reducer;
